import { categorySearchString } from '../../components/CategoryMenu/helpers';

/**
 * Footer pages that don't exist yet are wired to LandingPage as a
 * stand-in so every link resolves to a real route. When the client
 * ships real pages, swap `placeholder: true` for `name: 'TheirPage'`.
 */
export const PLACEHOLDER_ROUTE_NAME = 'LandingPage';

/**
 * Resolves a footer/trust link config to NamedLink props.
 *   - `placeholder: true` → LandingPage (fallback)
 *   - `keywords: '...'`   → SearchPage with `?keywords=...`
 *   - `name: '...'`       → that named route directly, with `search` when given
 */
export const resolveLinkProps = link => {
  if (!link) return { name: PLACEHOLDER_ROUTE_NAME };
  if (link.placeholder) {
    return { name: PLACEHOLDER_ROUTE_NAME };
  }
  if (link.keywords) {
    return {
      name: 'SearchPage',
      to: { search: `?keywords=${encodeURIComponent(link.keywords)}` },
    };
  }
  // `hash`: a place on that page, e.g. /faq#sortering. `search`: a query.
  const to = {
    ...(link.hash ? { hash: `#${link.hash}` } : {}),
    ...(link.search ? { search: link.search } : {}),
  };
  return {
    name: link.name,
    params: link.params,
    ...(Object.keys(to).length ? { to } : {}),
  };
};

/**
 * Footer content config — driven by the client's footer spec
 * (Zuprize_Footer_Spec_EN.pdf, May 2026).
 *
 * Four link columns + a payment row + a bottom bar with legal items
 * and social media labels. Each link item is one of:
 * - `{ name: 'RouteName', params? }` – resolved via NamedLink
 * - `{ keywords: '...' }` – navigates to SearchPage filtered by keywords
 * - `{ placeholder: true }` – falls back to LandingPage until the real
 *   route exists (the client will replace these with real pages later)
 *
 * Labels come from translation keys so editing copy stays a
 * translations-only change.
 */

// "Presse" and "Sirkulering" were removed from the about column
// (client request May 2026). Their translation keys stay in nb/en.json
// as harmless orphans in case the column ever needs to grow back.
export const aboutColumn = {
  titleId: 'Footer.aboutTitle',
  links: [
    { key: 'om-oss', labelMessageId: 'Footer.aboutOmOss', name: 'AboutPage' },
    { key: 'partnere', labelMessageId: 'Footer.aboutPartnere', name: 'PartnerPage' },
    { key: 'magasin', labelMessageId: 'Footer.aboutMagasin', name: 'MagasinPage' },
  ],
};

export const howItWorksColumn = {
  titleId: 'Footer.howItWorksTitle',
  links: [
    { key: 'slik-selger-du', labelMessageId: 'Footer.howItWorksSlikSelger', name: 'HowToSellPage' },
    { key: 'slik-kjoper-du', labelMessageId: 'Footer.howItWorksSlikKjoper', name: 'HowToBuyPage' },
    // A clean /kom-i-gang (Guro, 14 October): no ?fra=footer any more, so a
    // sign-up from here counts as `direct` in the sign_up event.
    { key: 'kom-i-gang', labelMessageId: 'Footer.aboutBliMedlem', name: 'BliMedlemPage' },
  ],
};

// The separate kjøpsvilkår/salgsvilkår documents were consolidated into a
// single unified Brukervilkår (User Terms) page (client request June 2026),
// so the trust column now carries one terms link.
export const trustColumn = {
  titleId: 'Footer.trustTitle',
  links: [
    { key: 'brukervilkar', labelMessageId: 'Footer.trustBrukervilkar', name: 'UserTermsPage' },
    { key: 'vurderinger', labelMessageId: 'Footer.howItWorksVurderinger', name: 'VurderingerPage' },
    // Each Zuprize Partner's own withdrawal form, on one page.
    { key: 'angreskjema', labelMessageId: 'Footer.trustAngreskjema', name: 'AngreskjemaPage' },
  ],
};

export const helpColumn = {
  titleId: 'Footer.helpTitle',
  links: [
    { key: 'faq', labelMessageId: 'Footer.helpFaq', name: 'FaqPage' },
    { key: 'kontakt', labelMessageId: 'Footer.helpKontakt', name: 'ContactPage' },
  ],
};

/**
 * "Populært": the main categories as plain links to their search pages, so
 * visitors and search engines reach them from every page (the desktop
 * flyout only renders the subcategories on hover). Guro, 10 October.
 * Each link names a category path, turned into the search address by
 * `categoryLinkProps`.
 */
export const popularColumn = {
  titleId: 'Footer.popularTitle',
  links: [
    {
      key: 'kjoler',
      labelMessageId: 'Footer.popularKjoler',
      categoryPath: ['klar', 'klar-kjoler'],
    },
    {
      key: 'gensere',
      labelMessageId: 'Footer.popularGensere',
      categoryPath: ['klar', 'klar-gensere'],
    },
    {
      key: 'bluser',
      labelMessageId: 'Footer.popularBluser',
      categoryPath: ['klar', 'klar-bluser'],
    },
  ],
};

/** NamedLink props for a category link: the search page filtered to it. */
export const categoryLinkProps = link => ({
  name: 'SearchPage',
  to: { search: categorySearchString(link.categoryPath) },
});

/**
 * Bottom-bar legal items — left side, separated by a middot. The first
 * two are link items (Personvern + Cookies) and the last two are static
 * text (org number + copyright). Personvern points at the existing
 * PrivacyPolicyPage route; Cookies is a placeholder until the page
 * exists.
 */
export const legalItems = [
  {
    key: 'personvern',
    type: 'link',
    labelMessageId: 'Footer.legalPersonvern',
    name: 'PrivacyPolicyPage',
  },
  {
    key: 'cookies',
    type: 'link',
    labelMessageId: 'Footer.legalCookies',
    name: 'CookiesPage',
  },
  { key: 'org', type: 'text', labelMessageId: 'Footer.legalOrgNumber' },
  { key: 'copyright', type: 'text', labelMessageId: 'Footer.copyright' },
];

/**
 * Social-media row — right side of the bottom bar. For launch the
 * client only wants Facebook and LinkedIn; Instagram can be added
 * back when (if) they activate that channel.
 */
export const socialLinks = [
  {
    key: 'facebook',
    labelMessageId: 'Footer.socialFacebook',
    href: 'https://www.facebook.com/zuprize',
  },
  {
    key: 'linkedin',
    labelMessageId: 'Footer.socialLinkedIn',
    href: 'https://www.linkedin.com/company/zuprize',
  },
];

/**
 * Payment-method badges shown in the dedicated payment row. Card
 * networks first (Visa, Mastercard), then the two wallets we accept at
 * checkout (Apple Pay, Google Pay), then the underlying processor
 * (Stripe), then the shipping partner (Posten). Vipps was removed as a
 * checkout option in May 2026 and replaced by Google Pay so that buyers
 * on Android get the same one-click experience iPhone users already have.
 */
export const paymentBadges = [
  { key: 'visa', src: '/static/images/payments/visa.svg', alt: 'Visa', position: 'left' },
  {
    key: 'mastercard',
    src: '/static/images/payments/mastercard.svg',
    alt: 'Mastercard',
    position: 'left',
  },
  {
    key: 'applepay',
    src: '/static/images/payments/applepay.svg',
    alt: 'Apple Pay',
    position: 'left',
  },
  {
    key: 'googlepay',
    src: '/static/images/payments/googlepay.svg',
    alt: 'Google Pay',
    position: 'left',
  },
  { key: 'stripe', src: '/static/images/payments/stripe.svg', alt: 'Stripe', position: 'left' },
  { key: 'posten', src: '/static/images/payments/posten.svg', alt: 'Posten', position: 'left' },
];
